import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AspectRatio,
  Box,
  Button,
  CloseButton,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { BrandAvatar } from "../../components/BrandAvatar";
import { MentionTextarea } from "./MentionTextarea";
import { getThemeKey } from "../utils/themesmap";
import { getThemes } from "../../services/themeService";

const API_BASE = "https://api.globalwineinstitute.org";
const API_KEY = "f3bb4afa55070cc052d1b888d4f7465a";
const URL_RE = /https?:\/\/[^\s]+/;

// All six GWBI regions (canonical names) + their i18n label keys, so the
// composer can tag a post with ANY region, not only the user's own.
const ALL_REGIONS = [
  "Western and Southern Europe",
  "Eastern Europe and Central Asia",
  "North America",
  "Latin America",
  "Africa and the Middle East",
  "Asia Pacific",
];
const REGION_LABEL_KEYS = {
  "Western and Southern Europe": "reg_western_eu",
  "Eastern Europe and Central Asia": "reg_eastern_eu",
  "North America": "reg_north_america",
  "Latin America": "reg_latin_america",
  "Africa and the Middle East": "reg_africa_me",
  "Asia Pacific": "reg_asia_pacific",
};

const useLinkPreview = (text) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastUrlRef = useRef("");
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setPreview(null);
  }, []);

  useEffect(() => {
    if (dismissed) return;
    const match = (text || "").match(URL_RE);
    const url = match ? match[0] : "";

    if (!url || url === lastUrlRef.current) return;
    lastUrlRef.current = url;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/link-preview?url=${encodeURIComponent(url)}`,
          { headers: { "X-API-KEY": API_KEY } }
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.title || data.image) {
          setPreview(data);
        }
      } catch {
        setPreview(null);
      } finally {
        setLoading(false);
      }
    }, 600);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [text, dismissed]);

  useEffect(() => {
    if (dismissed && !(text || "").match(URL_RE)) {
      setDismissed(false);
      lastUrlRef.current = "";
    }
  }, [text, dismissed]);

  return { preview, loading, dismiss };
};

export const PostComposer = ({
  profile,
  t,
  composerText,
  onComposerTextChange,
  onOpenComposer,
  onCloseComposer,
  isOpen,
  posting,
  onSubmit,
  mediaFile,
  mediaPreview,
  mediaType,
  onPhotoTrigger,
  onVideoTrigger,
  onPhotoSelected,
  onVideoSelected,
  onRemoveMedia,
  photoInputRef,
  videoInputRef,
  tags = [],
  onToggleTag,
  onClearTags,
}) => {
  const { preview: linkPreview, loading: linkLoading, dismiss: dismissPreview } = useLinkPreview(composerText);

  // Full topic list (all themes), loaded once — not just the user's.
  const [allThemes, setAllThemes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getThemes()
      .then((list) => {
        if (!cancelled && Array.isArray(list)) setAllThemes(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const regions = ALL_REGIONS;
  const regionLabel = (name) =>
    t(REGION_LABEL_KEYS[name] || "", { defaultValue: name });
  const topicLabel = (id) =>
    t(`themes.${getThemeKey({ id })}`, { defaultValue: getThemeKey({ id }) });
  const labelForToken = (token) => {
    const i = token.indexOf(":");
    const scope = token.slice(0, i);
    const key = token.slice(i + 1);
    return scope === "theme" ? topicLabel(key) : regionLabel(key);
  };

  const fallbackName =
    `${(profile?.first_name || "").trim()} ${(profile?.last_name || "").trim()}`.trim() ||
    t("dashboard.your_name");

  return (
    <>
      <Box bg="white" borderRadius="md" p={4} boxShadow="md" mb={4}>
        <HStack spacing={3}>
          <BrandAvatar
            size="md"
            name={fallbackName}
            src={profile?.avatar_url || "/avatar.jpg"}
          />
          <Input
            readOnly
            onClick={onOpenComposer}
            placeholder={t("dashboard.start_a_post")}
            borderRadius="full"
            bg="surface.subtle"
            _placeholder={{ color: "gray.500" }}
            cursor="pointer"
          />
        </HStack>
        <HStack spacing={6} mt={3} justify="space-around">
          <Button size="sm" variant="ghost" onClick={onOpenComposer}>
            {t("dashboard.photo")}
          </Button>
        </HStack>
      </Box>
      <Modal isOpen={isOpen} onClose={onCloseComposer} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("dashboard.create_post")}</ModalHeader>
          <ModalBody>
            <HStack spacing={3} mb={3} align="center">
              <BrandAvatar size="sm" name={fallbackName} src={profile?.avatar_url || "/avatar.jpg"} />
              <VStack spacing={0} align="start">
                <Text fontWeight="bold">
                  {fallbackName || t("dashboard.member")}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {t("dashboard.post_to_anyone")}
                </Text>
              </VStack>
            </HStack>

            <MentionTextarea
              value={composerText}
              onChange={(e) => onComposerTextChange(e.target.value)}
              placeholder="What do you want to talk about?"
              minH="160px"
            />

            {mediaPreview &&
              (mediaType === "image" ? (
                <Image
                  src={mediaPreview}
                  alt="preview"
                  mt={3}
                  maxH="320px"
                  objectFit="contain"
                />
              ) : (
                <AspectRatio ratio={16 / 9} mt={3}>
                  <video src={mediaPreview} controls />
                </AspectRatio>
              ))}

            {linkLoading && (
              <Skeleton height="80px" mt={3} borderRadius="md" />
            )}
            {linkPreview && !linkLoading && (
              <Box
                mt={3}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                overflow="hidden"
                position="relative"
              >
                <CloseButton
                  size="sm"
                  position="absolute"
                  top={1}
                  right={1}
                  zIndex={1}
                  bg="whiteAlpha.800"
                  onClick={dismissPreview}
                />
                {linkPreview.image && (
                  <Image
                    src={linkPreview.image}
                    alt=""
                    w="100%"
                    maxH="200px"
                    objectFit="cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <Box p={3} bg="gray.50">
                  {linkPreview.site_name && (
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                      {linkPreview.site_name}
                    </Text>
                  )}
                  {linkPreview.title && (
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
                      {linkPreview.title}
                    </Text>
                  )}
                  {linkPreview.description && (
                    <Text fontSize="xs" color="gray.600" noOfLines={2} mt={1}>
                      {linkPreview.description}
                    </Text>
                  )}
                </Box>
              </Box>
            )}

            <HStack spacing={4} mt={3}>
              <Button size="sm" variant="ghost" onClick={onPhotoTrigger}>
                {t("dashboard.photo")}
              </Button>
              {mediaFile && (
                <Button size="sm" variant="outline" onClick={onRemoveMedia}>
                  {t("dashboard.remove_media")}
                </Button>
              )}
            </HStack>

            {onToggleTag && (regions.length > 0 || allThemes.length > 0) && (
              <Box mt={3}>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  {t("dashboard.tag_post", "Tag this post (optional)")}
                </Text>

                {tags.length > 0 && (
                  <Wrap spacing={2} mb={2}>
                    {tags.map((tok) => (
                      <WrapItem key={tok}>
                        <Tag size="md" borderRadius="full" bg="gwbi.500" color="white">
                          <TagLabel>{labelForToken(tok)}</TagLabel>
                          <TagCloseButton onClick={() => onToggleTag(tok)} />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}

                <HStack spacing={2} align="center" wrap="wrap">
                  {regions.length > 0 && (
                    <Select
                      size="sm"
                      maxW="240px"
                      borderRadius="md"
                      value=""
                      placeholder={t("dashboard.add_region", "Add region…")}
                      onChange={(e) => {
                        if (e.target.value) onToggleTag(e.target.value);
                      }}
                    >
                      {regions
                        .filter((r) => !tags.includes(`region:${r}`))
                        .map((r) => (
                          <option key={r} value={`region:${r}`}>
                            {regionLabel(r)}
                          </option>
                        ))}
                    </Select>
                  )}
                  {allThemes.length > 0 && (
                    <Select
                      size="sm"
                      maxW="240px"
                      borderRadius="md"
                      value=""
                      placeholder={t("dashboard.add_topic", "Add topic…")}
                      onChange={(e) => {
                        if (e.target.value) onToggleTag(e.target.value);
                      }}
                    >
                      {allThemes
                        .filter((th) => !tags.includes(`theme:${String(th.id)}`))
                        .map((th) => (
                          <option key={String(th.id)} value={`theme:${String(th.id)}`}>
                            {topicLabel(String(th.id))}
                          </option>
                        ))}
                    </Select>
                  )}
                  {tags.length > 0 && (
                    <Button size="xs" variant="ghost" color="gray.500" onClick={onClearTags}>
                      {t("dashboard.clear", "Clear")}
                    </Button>
                  )}
                </HStack>
              </Box>
            )}

            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={onPhotoSelected}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              style={{ display: "none" }}
              onChange={onVideoSelected}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseComposer}>
              {t("cancel")}
            </Button>
            <Button
              colorScheme="gwbi"
              onClick={onSubmit}
              isLoading={posting}
              isDisabled={posting || (!composerText.trim() && !mediaFile)}
            >
              {t("dashboard.post")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
